import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { firestore, storage, auth } from "../firebaseConfig";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { collection, getDocs, query, where } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import { useAuthState } from "react-firebase-hooks/auth";
import { toast, ToastContainer } from "react-toastify"; // For toast notifications
import "react-toastify/dist/ReactToastify.css";
import { analytics } from "../firebaseConfig";
import { logEvent } from "firebase/analytics";
// Function to convert camelCase to Proper English
const toProperEnglish = (str) => {
  return str
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (match) => match.toUpperCase())
    .trim();
};

// Function to convert Proper English to camelCase
const toCamelCase = (str) => {
  return str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => (index === 0 ? word.toLowerCase() : word.toUpperCase()))
    .replace(/\s+/g, "");
};

const ContractDetail = () => {
  const { id } = useParams();
  const [user] = useAuthState(auth);
  const [contract, setContract] = useState(null);
  const [editedFields, setEditedFields] = useState({});
  const [fileUrl, setFileUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [role, setRole] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newFieldName, setNewFieldName] = useState("");

  useEffect(() => {
    logEvent(analytics, 'contract_detail_page');
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        if (user) {
          // const userQuery = query(collection(firestore, "users"), where("uid", "==", user.uid));
          // const userSnapshot = await getDocs(userQuery);
          const userQuery = doc(firestore, "users", user.uid)
          const userSnapshot = await getDoc(userQuery);
          if (userSnapshot.exists()) {
            const userData = userSnapshot.data();
            setRole(userData.role);
          }
        }

        const contractRef = doc(firestore, "contracts", id);
        const contractSnap = await getDoc(contractRef);

        if (contractSnap.exists()) {
          const data = contractSnap.data();
          // console.log(data)
          setContract(data);

          const flattenedSummary = data.summary || {};
          const formattedFields = {
            "File Name": data.fileName,
            "Status": data.status || "Processing",
            "Created At": data.createdAt ? data.createdAt.toDate().toLocaleString() : null,
            "Parties": flattenedSummary.parties || "",
            "Effective Date": flattenedSummary.effectiveDate || "",
            "Payment Amount": flattenedSummary.paymentAmount || "",
            "Payment Terms": flattenedSummary.paymentTerms || "",
            "Renewal Terms": flattenedSummary.renewalTerms || "",
          };
          setEditedFields(formattedFields);

          if (data.fileUrl && !fileUrl) {
            const url = await getDownloadURL(ref(storage, data.fileUrl));
            setFileUrl(url);
          }
        } else {
          setError("Contract not found.");
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load contract details.");
      }
      setLoading(false);
    };

    fetchData();
  }, [id, user]);

  const handleFieldChange = (field, value) => {
    setEditedFields((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddField = () => {
    if (newFieldName && !Object.keys(editedFields).includes(newFieldName)) {
      setEditedFields((prev) => ({ ...prev, [newFieldName]: "" }));
      setNewFieldName("");
      setIsModalOpen(false);
    } else if (Object.keys(editedFields).includes(newFieldName)) {
      toast.error("Field name already exists!");
    }
  };

  const handleDeleteField = (fieldToDelete) => {
    if (role === "admin" && window.confirm(`Are you sure you want to delete the field "${fieldToDelete}"?`)) {
      setEditedFields((prev) => {
        const newFields = { ...prev };
        delete newFields[fieldToDelete];
        return newFields;
      });
    }
  };

  const handleSave = async () => {
    if (!id) return;
    try {
      const camelCaseSummary = Object.fromEntries(
        Object.entries(editedFields)
          .filter(([key]) => !["File Name", "Status", "Created At"].includes(key))
          .map(([key, value]) => [toCamelCase(key), value])
      );

      const contractRef = doc(firestore, "contracts", id);
      await updateDoc(contractRef, {
        summary: camelCaseSummary,
      });
      toast.success("Contract details saved successfully!");
    } catch (error) {
      console.error("Error saving contract details:", error);
      toast.error("Failed to save contract details.");
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6 bg-gray-900 text-gray-200 min-h-screen">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-700 rounded w-1/4 mx-auto"></div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-96 bg-gray-700 rounded-lg"></div>
            <div className="space-y-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="flex items-center space-x-3">
                  <div className="h-4 bg-gray-700 rounded w-1/3"></div>
                  <div className="h-10 bg-gray-700 rounded flex-1"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto p-6 bg-gray-900 text-gray-200 min-h-screen">
        <p className="text-center text-red-500 bg-red-100 dark:bg-red-900 p-4 rounded-lg">{error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 bg-gray-900 text-gray-200 min-h-screen">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover />

      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-center text-blue-400">Contract Details</h2>
        <Link to="/" className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors">
          Back
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column - Preview */}
        <div className="bg-gray-800 p-6 rounded-lg shadow-md">
          <h3 className="text-xl font-semibold text-gray-100 mb-4">Contract Preview</h3>
          <div className="w-full h-[400px] lg:h-[500px] border border-gray-700 rounded-lg overflow-auto">
            {fileUrl ? (
              fileUrl.includes(".docx") || fileUrl.includes(".doc") ? (
                <div className="p-4 text-center text-gray-400">
                  <p>Word documents are not available for preview.</p>
                  <p>Please download the file to view it.</p>
                </div>
              ) : (
                <iframe
                  src={fileUrl}
                  title="Contract Preview"
                  className="w-full h-full"
                  onError={() => setError("Failed to load file preview.")}
                />
              )
            ) : (
              <div className="p-4 text-center text-gray-400">No file available for preview.</div>
            )}
          </div>
          <button
            onClick={() => window.open(fileUrl)}
            className="mt-4 w-full bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
            </svg>
            Download File
          </button>
        </div>

        {/* Right Column - Edit Fields */}
        <div className="bg-gray-800 p-6 rounded-lg shadow-md">
          <h3 className="text-xl font-semibold text-gray-100 mb-4">Edit Contract Details</h3>
          <div className="space-y-4 max-h-[400px] lg:max-h-[500px] overflow-y-auto pr-2">
            {Object.entries(editedFields).map(([key, value]) => (
              <div key={key} className="flex items-center space-x-3">
                <label className="text-sm font-semibold text-gray-300 w-1/3">{key}</label>
                <input
                  type="text"
                  className={`w-2/3 p-3 border border-gray-600 rounded-lg bg-gray-700 text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-600 ${
                    key === "Created At" || key === "File Name" || key === "Status"
                      ? "bg-gray-600 opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                  value={value || ""}
                  onChange={(e) => handleFieldChange(key, e.target.value)}
                  disabled={key === "Created At" || key === "File Name" || key === "Status"}
                />
                {role === "admin" && key !== "File Name" && key !== "Status" && key !== "Created At" && (
                  <button
                    onClick={() => handleDeleteField(key)}
                    className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className="mt-4 w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path>
            </svg>
            Add Field
          </button>
          <button
            onClick={handleSave}
            className="mt-4 w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Save Changes
          </button>
        </div>
      </div>

      {/* Modal for Adding New Field */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-gray-800 p-6 rounded-lg shadow-lg w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-100 mb-4">Add New Field</h3>
            <input
              type="text"
              placeholder="Enter field name"
              value={newFieldName}
              onChange={(e) => setNewFieldName(e.target.value)}
              className="w-full p-3 border border-gray-600 rounded-lg bg-gray-700 text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-600 mb-4"
            />
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddField}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractDetail;